import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { pick } from "@repo/utils/collections"
import { logger } from "@repo/utils/logger"
import { dataTableBaseSearchSchema } from "~/lib/schemas/common"
import { getExtendedClientQueryOptions } from "~/modules/clients/clients.queries"
import { ClientResourcesTable } from "~/modules/resources/components/client-resources-table"
import { getExtendedResourcesQueryOptions } from "~/modules/resources/resources.queries"

const log = logger.child({
  module: "clients-resources-route",
})
const searchSchema = dataTableBaseSearchSchema

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/clients/$clientSlug/resources",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) =>
    pick(search, ["q", "page", "page_size", "sorting", "columnFilters"]),
  loader: async ({ context, deps, params }) => {
    const { clientSlug } = params

    // Ensure client data is available
    context.queryClient
      .ensureQueryData(getExtendedClientQueryOptions({ clientSlug }))
      .catch((error) => {
        log.error("Error getting extended client", { error })
        return null
      })

    const filters = {
      client_slug: clientSlug,
      ...deps,
    }

    // Ensure resources data is available
    context.queryClient
      .ensureQueryData(getExtendedResourcesQueryOptions({ filters }))
      .catch((error) => {
        log.error("Error getting extended resources", { error })
        return null
      })

    return {
      filters,
    }
  },
})

function RouteComponent() {
  const { clientSlug } = Route.useParams()
  const search = Route.useSearch()
  const loaderData = Route.useLoaderData()

  const { data: clientData } = useSuspenseQuery(
    getExtendedClientQueryOptions({ clientSlug }),
  )

  // Use search params directly if loader data is not available
  const filters = loaderData?.filters || search

  const pagination = {
    total: 0, // Will be updated by the component when data loads
    page: filters.page || 1,
    page_size: filters.page_size || 20,
  }

  return (
    <div className="h-full flex-1 overflow-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="py-1 font-semibold text-lg text-white">Resources</h1>
        </div>
        <ClientResourcesTable
          clientSlug={clientSlug}
          clientName={clientData.name}
          pagination={pagination}
          initialColumnFilters={filters.columnFilters || []}
          initialSorting={filters.sorting || []}
        />
      </div>
    </div>
  )
}
